const MEMBERSHIP_ENROLLMENT_FLOW = 'membership_enrollment'
const MEMBERSHIP_ENROLLMENT_FEATURE = 'Membership enrollment'

export const KIOSK_MEMBERSHIP_ENROLLMENT_TRACKING_EVENT =
  'KIOSK_PLAN_MEMBERSHIP_ENROLL'

type ValueOf<T> = T[keyof T]

export const KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE = {
  ENGAGEMENT: 'engagement',
  IMPRESSION: 'impression',
} as const

export type KioskMembershipEnrollmentEventType = ValueOf<
  typeof KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE
>

export const KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME = {
  ADD_PLAN: 'add_plan',
  CONFIRM_EMAIL: 'confirm_email',
  PLAN_SUMMARY: 'plan_summary',
  SETUP_PLAN: 'setup_plan',
  TERMS: 'terms',
} as const

export type KioskMembershipEnrollmentStepName = ValueOf<
  typeof KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME
>

export const KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME = {
  BACK_CLICKED: 'back_clicked',
  CONFIRM_EMAIL_NEXT_CLICKED: 'confirm_email_next_clicked',
  SETUP_CONTINUE_CLICKED: 'setup_continue_clicked',
  SETUP_NEXT_CLICKED: 'setup_next_clicked',
  SETUP_SKIP_CLICKED: 'setup_skip_clicked',
  SIGN_UP_NOW_CLICKED: 'sign_up_now_clicked',
  SUMMARY_CONTINUE_CLICKED: 'summary_continue_clicked',
  TERMS_CHECKBOX_CLICKED: 'terms_checkbox_clicked',
  TERMS_CONTINUE_CLICKED: 'terms_continue_clicked',
  TERMS_LINK_CLICKED: 'terms_link_clicked',
  VIEW_FULL_DETAILS_CLICKED: 'view_full_details_clicked',
} as const

export type KioskMembershipEnrollmentActionName = ValueOf<
  typeof KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME
>

export const KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME = {
  ADD_PLAN_VIEWED: 'kiosk_membership_payment_capture_add_plan_viewed',
  BACK_CLICKED: 'kiosk_membership_payment_capture_back_clicked',
  CONFIRM_EMAIL_NEXT_CLICKED:
    'kiosk_membership_payment_capture_confirm_email_next_clicked',
  CONFIRM_EMAIL_VIEWED: 'kiosk_membership_payment_capture_confirm_email_viewed',
  PLAN_SUMMARY_VIEWED: 'kiosk_membership_payment_capture_plan_summary_viewed',
  SETUP_CONTINUE_CLICKED:
    'kiosk_membership_payment_capture_setup_continue_clicked',
  SETUP_NEXT_CLICKED: 'kiosk_membership_payment_capture_setup_next_clicked',
  SETUP_PLAN_VIEWED: 'kiosk_membership_payment_capture_setup_plan_viewed',
  SETUP_SKIP_CLICKED: 'kiosk_membership_payment_capture_setup_skip_clicked',
  SIGN_UP_NOW_CLICKED: 'kiosk_membership_payment_capture_sign_up_now_clicked',
  SUMMARY_CONTINUE_CLICKED:
    'kiosk_membership_payment_capture_summary_continue_clicked',
  TERMS_CHECKBOX_CLICKED:
    'kiosk_membership_payment_capture_terms_checkbox_clicked',
  TERMS_CONTINUE_CLICKED:
    'kiosk_membership_payment_capture_terms_continue_clicked',
  TERMS_LINK_CLICKED: 'kiosk_membership_payment_capture_terms_link_clicked',
  TERMS_VIEWED: 'kiosk_membership_payment_capture_terms_viewed',
  VIEW_FULL_DETAILS_CLICKED:
    'kiosk_membership_payment_capture_view_full_details_clicked',
} as const

export type KioskMembershipEnrollmentEventName = ValueOf<
  typeof KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME
>

export interface KioskMembershipEnrollmentAnalyticsConfig {
  actionName?: KioskMembershipEnrollmentActionName
  description: string
  eventCategory: string
  eventLabel: string
  eventName: KioskMembershipEnrollmentEventName
  eventType: KioskMembershipEnrollmentEventType
  interactionId: string
  stepName?: KioskMembershipEnrollmentStepName
  stepNumber?: number
}

export const KIOSK_MEMBERSHIP_ENROLLMENT_ANALYTICS = {
  ADD_PLAN_VIEWED: {
    actionName: undefined,
    description: 'User views the add a membership plan step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Viewed - Add a Membership Plan',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.ADD_PLAN_VIEWED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.IMPRESSION,
    interactionId: '4',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.ADD_PLAN,
    stepNumber: 3,
  },
  VIEW_FULL_DETAILS_CLICKED: {
    actionName:
      KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.VIEW_FULL_DETAILS_CLICKED,
    description: 'User opens plan details from the add plan step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - View Full Details',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.VIEW_FULL_DETAILS_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '5',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.ADD_PLAN,
    stepNumber: 3,
  },
  SIGN_UP_NOW_CLICKED: {
    actionName: KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.SIGN_UP_NOW_CLICKED,
    description: 'User continues from add plan into set up your plan',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Sign Up Now',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.SIGN_UP_NOW_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '6',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.ADD_PLAN,
    stepNumber: 3,
  },
  SETUP_PLAN_VIEWED: {
    actionName: undefined,
    description: 'User views the set up your plan step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Viewed - Set Up Your Plan',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.SETUP_PLAN_VIEWED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.IMPRESSION,
    interactionId: '7',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.SETUP_PLAN,
    stepNumber: 4,
  },
  SETUP_NEXT_CLICKED: {
    actionName: KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.SETUP_NEXT_CLICKED,
    description: 'User moves to the next patient while setting up plans',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Setup Next',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.SETUP_NEXT_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '8',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.SETUP_PLAN,
    stepNumber: 4,
  },
  SETUP_SKIP_CLICKED: {
    actionName: KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.SETUP_SKIP_CLICKED,
    description: 'User skips ahead from the set up your plan step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Setup Skip',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.SETUP_SKIP_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '9',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.SETUP_PLAN,
    stepNumber: 4,
  },
  SETUP_CONTINUE_CLICKED: {
    actionName: KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.SETUP_CONTINUE_CLICKED,
    description: 'User continues from the set up your plan step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Setup Continue',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.SETUP_CONTINUE_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '10',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.SETUP_PLAN,
    stepNumber: 4,
  },
  PLAN_SUMMARY_VIEWED: {
    actionName: undefined,
    description: 'User views the plan summary step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Viewed - Plan Summary',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.PLAN_SUMMARY_VIEWED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.IMPRESSION,
    interactionId: '11',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.PLAN_SUMMARY,
    stepNumber: 5,
  },
  TERMS_LINK_CLICKED: {
    actionName: KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.TERMS_LINK_CLICKED,
    description: 'User opens terms and conditions from the plan summary step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Terms Link',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.TERMS_LINK_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '12',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.PLAN_SUMMARY,
    stepNumber: 5,
  },
  SUMMARY_CONTINUE_CLICKED: {
    actionName:
      KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.SUMMARY_CONTINUE_CLICKED,
    description: 'User continues from the plan summary step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Summary Continue',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.SUMMARY_CONTINUE_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '13',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.PLAN_SUMMARY,
    stepNumber: 5,
  },
  TERMS_VIEWED: {
    actionName: undefined,
    description: 'User views the terms and conditions step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Viewed - Terms and Conditions',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.TERMS_VIEWED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.IMPRESSION,
    interactionId: '14',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.TERMS,
    stepNumber: 6,
  },
  TERMS_CHECKBOX_CLICKED: {
    actionName: KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.TERMS_CHECKBOX_CLICKED,
    description: 'User toggles the terms and conditions checkbox',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Terms Checkbox',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.TERMS_CHECKBOX_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '15',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.TERMS,
    stepNumber: 6,
  },
  TERMS_CONTINUE_CLICKED: {
    actionName: KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.TERMS_CONTINUE_CLICKED,
    description: 'User continues from the terms and conditions step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Terms Continue',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.TERMS_CONTINUE_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '16',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.TERMS,
    stepNumber: 6,
  },
  CONFIRM_EMAIL_VIEWED: {
    actionName: undefined,
    description: 'User views the confirm client email step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Viewed - Confirm Client Email',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.CONFIRM_EMAIL_VIEWED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.IMPRESSION,
    interactionId: '17',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.CONFIRM_EMAIL,
    stepNumber: 7,
  },
  CONFIRM_EMAIL_NEXT_CLICKED: {
    actionName:
      KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.CONFIRM_EMAIL_NEXT_CLICKED,
    description: 'User submits the confirm client email step',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Confirm Client Email Next',
    eventName:
      KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.CONFIRM_EMAIL_NEXT_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '18',
    stepName: KIOSK_MEMBERSHIP_ENROLLMENT_STEP_NAME.CONFIRM_EMAIL,
    stepNumber: 7,
  },
  BACK_CLICKED: {
    actionName: KIOSK_MEMBERSHIP_ENROLLMENT_ACTION_NAME.BACK_CLICKED,
    description: 'User clicks back during membership enrollment',
    eventCategory: 'Membership Enrollment',
    eventLabel: 'Click - Back',
    eventName: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_NAME.BACK_CLICKED,
    eventType: KIOSK_MEMBERSHIP_ENROLLMENT_EVENT_TYPE.ENGAGEMENT,
    interactionId: '19',
  },
} as const satisfies Record<string, KioskMembershipEnrollmentAnalyticsConfig>

export type KioskMembershipEnrollmentAnalyticsKey =
  keyof typeof KIOSK_MEMBERSHIP_ENROLLMENT_ANALYTICS

export interface KioskMembershipEnrollmentTrackingOverrides {
  actionName?: KioskMembershipEnrollmentActionName
  additionalProperties?: Record<string, unknown>
  elapsedTimeMs?: number
  errorCode?: string | null
  errorType?: string | null
  eventType?: KioskMembershipEnrollmentEventType
  stepName?: KioskMembershipEnrollmentStepName
  stepNumber?: number
  success?: boolean
}

interface BuildKioskMembershipEnrollmentTrackingDataParams {
  businessId?: string
  clientId?: string
  eventKey: KioskMembershipEnrollmentAnalyticsKey
  flowId?: string
  flowStartedAt?: number | null
  interactionOrderId: number
  overrides?: KioskMembershipEnrollmentTrackingOverrides
  selectedPatientCount?: number
}

export const buildKioskMembershipEnrollmentTrackingData = ({
  businessId,
  clientId,
  eventKey,
  flowId,
  flowStartedAt,
  interactionOrderId,
  overrides = {},
  selectedPatientCount = 0,
}: BuildKioskMembershipEnrollmentTrackingDataParams) => {
  const analyticsConfig = KIOSK_MEMBERSHIP_ENROLLMENT_ANALYTICS[eventKey]
  const elapsedTimeMs =
    overrides.elapsedTimeMs ??
    (flowStartedAt ? Date.now() - flowStartedAt : undefined)

  return {
    feature: MEMBERSHIP_ENROLLMENT_FEATURE,
    flow: MEMBERSHIP_ENROLLMENT_FLOW,
    flow_id: flowId,
    interactionId: analyticsConfig.interactionId,
    interaction_order_id: interactionOrderId,
    app_source: 'kiosk',
    eventCategory: analyticsConfig.eventCategory,
    eventLabel: analyticsConfig.eventLabel,
    description: analyticsConfig.description,
    event_name: analyticsConfig.eventName,
    event_type: overrides.eventType ?? analyticsConfig.eventType,
    event_action: overrides.actionName ?? analyticsConfig.actionName,
    action_name: overrides.actionName ?? analyticsConfig.actionName,
    step_name:
      overrides.stepName ??
      ('stepName' in analyticsConfig ? analyticsConfig.stepName : undefined),
    step_number:
      overrides.stepNumber ??
      ('stepNumber' in analyticsConfig
        ? analyticsConfig.stepNumber
        : undefined),
    business_id: businessId || undefined,
    client_id: clientId || undefined,
    selected_patient_count: selectedPatientCount,
    elapsed_time_ms: elapsedTimeMs,
    success: overrides.success,
    error_code: overrides.errorCode ?? undefined,
    error_type: overrides.errorType ?? undefined,
    ...overrides.additionalProperties,
  }
}
